import { defineStyleConfig } from '@chakra-ui/react';
import {
  mode,
  SystemStyleFunction,
  SystemStyleObject,
} from '@chakra-ui/theme-tools';
import { merge } from 'lodash';

import { ColorSchemeStruct } from '../types';

const PrimaryVariant: SystemStyleFunction = (props) => {
  const colorScheme = ColorSchemeStruct.parse(props.colorScheme);

  const base: SystemStyleObject = {
    bgColor: mode(`${colorScheme}.500`, `${colorScheme}.200`)(props),
    color: mode('white', 'gray.950')(props),
    borderColor: mode(`${colorScheme}.500`, `${colorScheme}.200`)(props),
    borderStyle: 'solid',
    borderWidth: '2px',
  };
  const _hover: SystemStyleObject = {
    bgColor: mode(`${colorScheme}.600`, `${colorScheme}.100`)(props),
    borderColor: mode(`${colorScheme}.600`, `${colorScheme}.100`)(props),
    _disabled: {
      bgColor: base.bgColor,
      borderColor: base.borderColor,
    },
  };
  const _active: SystemStyleObject = {
    bgColor: mode(`${colorScheme}.700`, `${colorScheme}.50`)(props),
    borderColor: mode(`${colorScheme}.700`, `${colorScheme}.50`)(props),
  };
  const _disabled: SystemStyleObject = {
    bgColor: base.bgColor,
    borderColor: base.borderColor,
    cursor: 'not-allowed',
    opacity: 0.4,
  };

  return { ...base, _hover, _active, _disabled };
};
const PrimaryMutedVariant: SystemStyleFunction = (props) => {
  const colorScheme = ColorSchemeStruct.parse(props.colorScheme);

  const base: SystemStyleObject = {
    bgColor: mode(`${colorScheme}.50`, `${colorScheme}.800`)(props),
    borderColor: mode(`${colorScheme}.50`, `${colorScheme}.800`)(props),
    color: mode(`${colorScheme}.800`, `${colorScheme}.100`)(props),
  };
  const _hover: SystemStyleObject = {
    bgColor: mode(`${colorScheme}.100`, `${colorScheme}.700`)(props),
    borderColor: mode(`${colorScheme}.100`, `${colorScheme}.700`)(props),
    _disabled: {
      bgColor: base.bgColor,
      borderColor: base.borderColor,
      color: base.color,
    },
  };
  const _active: SystemStyleObject = {
    color: mode('white', 'gray.950')(props),
  };
  const _disabled: SystemStyleObject = {
    bgColor: base.bgColor,
    borderColor: base.borderColor,
    color: base.color,
  };

  return merge(PrimaryVariant(props), { ...base, _hover, _active, _disabled });
};

const SecondaryVariant: SystemStyleFunction = (props) => {
  const colorScheme = ColorSchemeStruct.parse(props.colorScheme);
  const base: SystemStyleObject = {
    bgColor: 'transparent',
    color: mode(`${colorScheme}.800`, `${colorScheme}.100`)(props),
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: mode(`${colorScheme}.500`, `${colorScheme}.200`)(props),
  };

  const _hover: SystemStyleObject = {
    bgColor: mode(`${colorScheme}.50`, `${colorScheme}.800`)(props),
    _disabled: {
      bgColor: base.bgColor,
    },
  };
  const _active: SystemStyleObject = {
    bgColor: mode(`${colorScheme}.100`, `${colorScheme}.700`)(props),
  };
  const _disabled: SystemStyleObject = {
    bgColor: base.bgColor,
    cursor: 'not-allowed',
    opacity: 0.4,
  };

  return { ...base, _hover, _active, _disabled };
};
const SecondaryMutedVariant: SystemStyleFunction = (props) => {
  const colorScheme = ColorSchemeStruct.parse(props.colorScheme);

  const base: SystemStyleObject = {
    borderWidth: '1px',
    borderColor: mode(`${colorScheme}.200`, `${colorScheme}.600`)(props),
  };

  const _hover: SystemStyleObject = {
    borderColor: mode(`${colorScheme}.400`, `${colorScheme}.400`)(props),
    _disabled: {
      borderColor: base.borderColor,
    },
  };
  const _active: SystemStyleObject = {
    borderColor: mode(`${colorScheme}.600`, `${colorScheme}.200`)(props),
  };
  const _disabled: SystemStyleObject = {
    borderColor: base.borderColor,
    cursor: 'not-allowed',
    opacity: 0.4,
  };

  return merge(SecondaryVariant(props), {
    ...base,
    _hover,
    _active,
    _disabled,
  });
};

const V2GhostVariant: SystemStyleFunction = (props) => {
  const colorScheme = ColorSchemeStruct.parse(props.colorScheme);
  const base: SystemStyleObject = {
    bgColor: 'transparent',
    color: mode(`${colorScheme}.800`, `${colorScheme}.100`)(props),
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: 'transparent',
  };

  const _hover: SystemStyleObject = {
    bgColor: mode(`${colorScheme}.50`, `${colorScheme}.800`)(props),
    _disabled: {
      bgColor: base.bgColor,
    },
  };
  const _active: SystemStyleObject = {
    bgColor: mode(`${colorScheme}.100`, `${colorScheme}.700`)(props),
  };
  const _disabled: SystemStyleObject = {
    bgColor: base.bgColor,
    cursor: 'not-allowed',
    opacity: 0.4,
  };

  return { ...base, _hover, _active, _disabled };
};

// Exports
export const Button = defineStyleConfig({
  baseStyle: {
    fontWeight: 'bold',
  },
  variants: {
    link: {
      textDecoration: 'underline',
      _hover: {
        textDecoration: 'underline',
        _disabled: {
          textDecoration: 'underline',
        },
      },
    },

    primary: PrimaryVariant,
    'primary-muted': PrimaryMutedVariant,
    secondary: SecondaryVariant,
    'secondary-muted': SecondaryMutedVariant,
    'v2-ghost': V2GhostVariant,
  },
});

export type ButtonVariant =
  | 'primary'
  | 'primary-muted'
  | 'secondary'
  | 'secondary-muted'
  | 'v2-ghost';
